import * as React from 'react';

import { sortBy, reverse } from 'lodash';
import { InjectedRouter } from 'react-router';

import SmartTable from 'components/SmartTable';
import StatusText from 'components/StatusText';
import TableCell from 'components/TableCell';
import TableRow from 'components/TableRow';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';
import sisSyncPath from 'admin/lib/sisSyncPath';

import {
  AdminDistrictSisDashboard_organizations_rosteringPermissions as RosteringPermissions,
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  orgUuid: string;
  orgId: number;
  rosteringPermissions: RosteringPermissions;
  syncMethod: string;
  router: InjectedRouter;
  childOrgs: ChildOrganization[];
};

const nameSorts = [
  { key: 'name_asc', name: 'Ascending' },
  { key: 'name_desc', name: 'Descending' },
];

const errorSorts = [
  { key: 'error_asc', name: 'Ascending' },
  { key: 'error_desc', name: 'Descending' },
];

const alertSorts = [
  { key: 'alert_asc', name: 'Ascending' },
  { key: 'alert_desc', name: 'Descending' },
];

const statusSorts = [
  { key: 'status_asc', name: 'Ascending' },
  { key: 'status_desc', name: 'Descending' },
];

const errorTooltip =
  "Errors are mistakes in your SIS that prevent student or teacher accounts from being created. It's critical you address these as soon as possible.";
const alertTooltip =
  'Alerts are minor data errors in your SIS that, if fixed, would make Remind more useful and productive to use.';

const COLUMNS = [
  { key: 'name', name: 'School', sorts: nameSorts },
  { key: 'teachers', name: 'Teachers' },
  { key: 'students', name: 'Students' },
  { key: 'errors', name: 'Errors', sorts: errorSorts, tooltip: errorTooltip },
  { key: 'alerts', name: 'Alerts', sorts: alertSorts, tooltip: alertTooltip },
  { key: 'sync_status', name: 'Sync status', sorts: statusSorts },
];

const IN_REVIEW_COLUMNS = [
  { key: 'name', name: 'School', sorts: nameSorts },
  { key: 'teachers', name: 'Teachers' },
  { key: 'students', name: 'Students' },
  { key: 'errors', name: 'Errors' },
  { key: 'alerts', name: 'Alerts' },
  { key: 'sync_status', name: 'Sync status', sorts: statusSorts },
];

export function sortOrgs(orgs: ChildOrganization[], sort?: string): ChildOrganization[] {
  let iteratees: ((o: ChildOrganization) => string | boolean | undefined | number | null)[] = [
    o => o?.sisSyncOverview?.syncStatus === 'on_hold',
    o => o?.sisSyncOverview?.errorCount,
    o => o?.sisSyncOverview?.syncStatus !== 'not_set_up',
  ];
  let descending = true;

  if (sort) {
    if (sort.endsWith('_asc')) {
      descending = false;
    }

    if (sort.startsWith('name_')) {
      iteratees = [o => o?.name];
    } else if (sort.startsWith('error_')) {
      iteratees = [o => o?.sisSyncOverview?.errorCount];
    } else if (sort.startsWith('alert_')) {
      iteratees = [o => o?.sisSyncOverview?.alertCount];
    } else if (sort.startsWith('status_')) {
      iteratees = [o => o?.sisSyncOverview?.syncStatus];
    }
  }

  if (typeof orgs === 'undefined' || orgs === null || orgs.length < 1) {
    return [];
  }

  const sortedOrgs = sortBy<ChildOrganization>([...orgs], iteratees);

  return descending ? reverse([...sortedOrgs]) : sortedOrgs;
}

export default class AdminSisSyncSchoolTable extends React.Component<Props> {
  renderErrorName(name: string) {
    return (
      <div className="AdminDistrictSisDashboard-errorName">
        <TextWithGlyph
          glyph="explanation-circle"
          glyphColor="lava"
          glyphSize="large"
          gutter="xsmall"
          size="xxsmall"
          color="lava"
          weight="semibold">
          {name}
        </TextWithGlyph>
      </div>
    );
  }

  renderInReviewName(name: string) {
    return (
      <div className="AdminDistrictSisDashboard-errorName">
        <TextWithGlyph
          glyph="clock"
          glyphColor="koala"
          glyphSize="large"
          gutter="xsmall"
          size="small"
          color="midnight"
          weight="normal">
          {name}
        </TextWithGlyph>
      </div>
    );
  }

  renderSyncStatus(status: string) {
    if (status === 'not_set_up') {
      return 'Not connected';
    }
    return (
      <StatusText
        data-test={`status-text-${status}`}
        success={status === 'connected'}
        failure={status === 'on_hold'}>
        {status.replace('_', ' ')}
      </StatusText>
    );
  }

  formatSchoolName(
    child: ChildOrganization,
    canOnlySeeReviewScreen: boolean,
    hasErrors: boolean,
    isOnHold: boolean,
    notSetUp: boolean
  ) {
    if (canOnlySeeReviewScreen) {
      return notSetUp ? child.name : this.renderInReviewName(child.name);
    }
    return hasErrors || isOnHold ? this.renderErrorName(child.name) : child.name;
  }

  formatTableRow(child: ChildOrganization, canOnlySeeReviewScreen: boolean) {
    const { router } = this.props;

    if (!child || !child.paidAt) {
      return null;
    }
    const { sisSyncOverview } = child;
    const childId = child.id || '';
    const syncStatus = sisSyncOverview ? sisSyncOverview.syncStatus : '';
    const isOnHold = syncStatus === 'on_hold';
    const hasErrors = (sisSyncOverview ? sisSyncOverview.errorCount : 0) > 0;
    const notSetUp = syncStatus === 'not_set_up';
    const noData = syncStatus === 'processing' || notSetUp;
    const onRowClick = () => {
      if (!notSetUp) {
        router.push({ pathname: sisSyncPath(childId) });
      }
    };
    const errorCount = sisSyncOverview ? sisSyncOverview.errorCount : null;
    return (
      <TableRow hoverable key={child.uuid} onClick={onRowClick}>
        <td>
          {this.formatSchoolName(child, canOnlySeeReviewScreen, hasErrors, isOnHold, notSetUp)}
        </td>
        <TableCell empty={canOnlySeeReviewScreen || noData}>
          {sisSyncOverview ? sisSyncOverview.teacherCount : null}
        </TableCell>
        <TableCell empty={canOnlySeeReviewScreen || noData}>
          {sisSyncOverview ? sisSyncOverview.studentCount : null}
        </TableCell>
        <TableCell empty={canOnlySeeReviewScreen || noData}>
          {hasErrors ? (
            <Text weight="semibold" color="lava">
              {errorCount}
            </Text>
          ) : (
            errorCount
          )}
        </TableCell>
        <TableCell empty={canOnlySeeReviewScreen || noData}>
          {sisSyncOverview ? sisSyncOverview.alertCount : null}
        </TableCell>
        <td>{this.renderSyncStatus(syncStatus || '')}</td>
      </TableRow>
    );
  }

  render() {
    const { rosteringPermissions, orgId, childOrgs } = this.props;
    const { canOnlySeeReviewScreen } = rosteringPermissions;

    const namespace = canOnlySeeReviewScreen
      ? `admin-sis-dashboard-in-review-school-list-${orgId}`
      : `admin-sis-dashboard-school-list-${orgId}`;

    return (
      <SmartTable
        columns={canOnlySeeReviewScreen ? IN_REVIEW_COLUMNS : COLUMNS}
        namespace={namespace}>
        {({ sort }: { sort?: string }) => {
          return (
            <tbody>
              {sortOrgs(childOrgs, sort).map((child: ChildOrganization) => {
                return this.formatTableRow(child, canOnlySeeReviewScreen);
              })}
            </tbody>
          );
        }}
      </SmartTable>
    );
  }
}
